import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  DatePicker,
  Button,
  message,
  InputNumber,
  Select,
  Spin,
  Alert,
} from 'antd';
import { ArrowDownOutlined, InfoCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import TransactionService, {
  MaturityScheduleTransactionRequest,
} from '../../service/transaction-service/Transaction.service';
import POSDeviceService, {
  OptimalPOSResponse,
} from '../../service/pos-device-service/POS-device.service';
import { MaturityScheduleResponse } from '../../model/MaturitySchedule.model';
import useEmployee from '../../hook/useEmployee';
import useRole from '../../hook/useRole';

interface WithdrawalModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  maturitySchedule: MaturityScheduleResponse;
}

const WithdrawalModal: React.FC<WithdrawalModalProps> = ({
  visible,
  onClose,
  onSuccess,
  maturitySchedule,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [posLoading, setPosLoading] = useState(false);
  const [optimalPOSList, setOptimalPOSList] = useState<OptimalPOSResponse[]>(
    []
  );
  const { employee } = useEmployee();
  const role = useRole();

  // Load optimal POS devices when modal opens
  useEffect(() => {
    if (visible && maturitySchedule?.creditCard?.id) {
      loadOptimalPOSDevices();
    }
  }, [visible, maturitySchedule]);

  const loadOptimalPOSDevices = async () => {
    try {
      setPosLoading(true);
      const posDevices = await POSDeviceService.getOptimalPOSDevicesForCard(
        maturitySchedule.creditCard.id
      );
      setOptimalPOSList(posDevices);
    } catch (error) {
      message.error('Không thể tải danh sách POS');
      console.error('Error loading optimal POS devices:', error);
    } finally {
      setPosLoading(false);
    }
  };

  const handlePOSSelection = (posId: number) => {
    const selectedPOS = optimalPOSList.find((pos) => pos.posId === posId);
    if (selectedPOS) {
      console.log('Selected POS:', selectedPOS);
      console.log('Fee Rate:', selectedPOS.feeRate);

      // Phí áp dụng = chính số phần trăm của POS
      // VD: POS có phí 2.2% thì feeApplied = 2.2
      form.setFieldsValue({
        feeApplied: selectedPOS.feeRate,
      });
    }
  };

  // Không cần tính toán lại phí khi số tiền thay đổi
  // vì phí áp dụng là số phần trăm cố định của POS
  const handleAmountChange = (value: number | null) => {
    // Không làm gì cả, phí áp dụng là % cố định từ POS
  };

  const handleSubmit = async (values: any) => {
    try {
      if (!employee?.id) {
        message.error('Không tìm thấy thông tin nhân viên');
        return;
      }

      setLoading(true);

      const request: MaturityScheduleTransactionRequest = {
        maturityScheduleId: maturitySchedule.id,
        posDeviceId: values.posDeviceId,
        transactionDate: values.transactionDate.format('YYYY-MM-DD'),
        amount: values.amount,
        transactionType: 'withdrawal',
        feeApplied: values.feeApplied || 0,
        status: values.status,
        transactionFor: 'maturity',
      };

      const response =
        await TransactionService.createTransactionForMaturitySchedule(
          request,
          employee.id
        );

      if (response.success) {
        message.success(
          response.message || 'Tạo giao dịch rút tiền thành công!'
        );
        onSuccess();
        handleClose();
      } else {
        message.error(response.error || 'Có lỗi xảy ra khi tạo giao dịch');
      }
    } catch (error: any) {
      console.error('Error creating withdrawal transaction:', error);
      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        'Có lỗi xảy ra khi tạo giao dịch';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setOptimalPOSList([]);
    onClose();
  };

  const formatCardNumber = (cardNumber: string) => {
    if (!cardNumber) return 'N/A';
    const lastFour = cardNumber.slice(-4);
    return `***${lastFour}`;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
    }).format(amount);
  };

  const formatPercentage = (rate: number) => {
    // rate đã là percentage (VD: 1.2), không cần nhân 100
    return `${rate.toFixed(2)}%`;
  };

  const getAccountTypeText = () => {
    return role === 'ROLE_ADMIN' ? 'Tài khoản công ty' : 'Tài khoản cá nhân';
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'Đang chờ';
      case 'OVERDUE':
        return 'Quá hạn';
      case 'COMPLETED':
        return 'Hoàn thành';
      case 'PAID':
        return 'Đã nạp đáo';
      default:
        return status;
    }
  };

  return (
    <Modal
      title={
        <div className="flex items-center space-x-1">
          <ArrowDownOutlined className="text-blue-500 text-sm" />
          <span className="text-sm">Tạo GD rút tiền</span>
        </div>
      }
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={600}
    >
      {/* Search Guidance */}
      <Alert
        message={<span className="text-xs">Hướng dẫn tìm POS</span>}
        description={
          <div className="text-xs">
            <p>Tìm theo: Mã POS, Ngân hàng, Vị trí. POS tối ưu hiển thị đầu.</p>
          </div>
        }
        type="info"
        showIcon
        className="mb-3"
      />

      {/* Maturity Schedule Info */}
      <div className="bg-gray-50 p-2 rounded-lg mb-3">
        <h4 className="font-semibold mb-1 text-xs">Thông tin kỳ đáo hạn</h4>
        <div className="grid grid-cols-2 gap-1 text-xs">
          <div>
            <span className="text-gray-600">Thẻ tín dụng:</span>{' '}
            <span className="font-medium">
              {formatCardNumber(maturitySchedule.creditCard?.cardNumber)}
            </span>
          </div>
          <div>
            <span className="text-gray-600">Ngày đáo hạn:</span>{' '}
            <span className="font-medium">
              {dayjs(maturitySchedule.dueDate).format('DD-MM-YYYY')}
            </span>
          </div>
          <div>
            <span className="text-gray-600">Số tiền đáo hạn:</span>{' '}
            <span className="font-medium text-blue-600">
              {formatCurrency(maturitySchedule.repaymentAmount)}
            </span>
          </div>
          <div>
            <span className="text-gray-600">Trạng thái:</span>{' '}
            <span className="font-medium">
              {getStatusText(maturitySchedule.status)}
            </span>
          </div>
        </div>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          transactionDate: dayjs(),
          status: 'PENDING',
        }}
      >
        <div className="grid grid-cols-2 gap-2">
          {/* POS Device Selection */}
          <Form.Item
            label={<span className="text-xs">Chọn POS</span>}
            name="posDeviceId"
            rules={[{ required: true, message: 'Vui lòng chọn thiết bị POS' }]}
            className="col-span-2"
          >
            <Select
              placeholder="Tìm kiếm và chọn thiết bị POS tối ưu"
              loading={posLoading}
              onSelect={handlePOSSelection}
              showSearch
              optionFilterProp="label"
              notFoundContent={
                posLoading ? <Spin size="small" /> : 'Không có POS nào phù hợp'
              }
              optionLabelProp="label"
            >
              {optimalPOSList
                .sort((a, b) => {
                  // Sắp xếp ưu tiên cân bằng tải: POS ít dùng lên trước, phí là yếu tố thứ hai
                  const aTransactions = a.transactionCount || 0;
                  const bTransactions = b.transactionCount || 0;

                  // So sánh số giao dịch trước (ít giao dịch lên trước)
                  const transactionCompare = aTransactions - bTransactions;
                  if (transactionCompare !== 0) return transactionCompare;

                  // Nếu số giao dịch bằng nhau, mới so sánh phí (phí rẻ lên trước)
                  return a.feeRate - b.feeRate;
                })
                .map((pos, index) => (
                  <Select.Option
                    key={pos.posId}
                    value={pos.posId}
                    label={`${pos.deviceCode} - ${pos.bank.name} - Phí: ${pos.feeRate}% (${pos.transactionCount || 0} giao dịch)`}
                  >
                    <div className="flex justify-between items-center py-1">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <span className="font-bold text-gray-800">
                            {pos.deviceCode}
                          </span>
                          {index === 0 && (
                            <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                              Tối ưu nhất
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-600">
                          {pos.bank.name}
                          {pos.location && ` • ${pos.location}`}
                        </div>
                      </div>
                      <div className="text-right ml-4">
                        <div className="flex items-center space-x-3">
                          <div className="text-center">
                            <div className="text-green-600 font-bold text-sm">
                              {formatPercentage(pos.feeRate)}
                            </div>
                            <div className="text-xs text-gray-500">Phí</div>
                          </div>
                          <div className="text-center">
                            <div className="text-blue-600 font-bold text-sm">
                              {pos.transactionCount || 0}
                            </div>
                            <div className="text-xs text-gray-500">
                              Giao dịch
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>

          {/* Transaction Date */}
          <Form.Item
            label="Ngày giao dịch"
            name="transactionDate"
            rules={[
              { required: true, message: 'Vui lòng chọn ngày giao dịch' },
            ]}
          >
            <DatePicker
              placeholder="Chọn ngày giao dịch"
              className="w-full"
              format="DD/MM/YYYY"
              disabledDate={(current) => {
                if (!current) return false;
                const today = dayjs().startOf('day');
                const min = today.subtract(10, 'day');
                const max = today.add(10, 'day');
                return current.isBefore(min, 'day') || current.isAfter(max, 'day');
              }}
            />
          </Form.Item>

          {/* Status Selection */}
          <Form.Item
            label="Trạng thái giao dịch"
            name="status"
            rules={[{ required: true, message: 'Vui lòng chọn trạng thái' }]}
            tooltip="PENDING: Chưa cộng tiền vào tài khoản. COMPLETED: Cộng tiền ngay vào tài khoản."
          >
            <Select placeholder="Chọn trạng thái giao dịch">
              <Select.Option value="PENDING">
                <div className="flex items-center">
                  <span className="w-2 h-2 bg-yellow-500 rounded-full mr-2"></span>
                  PENDING - Chờ xử lý
                </div>
              </Select.Option>
              <Select.Option value="COMPLETED">
                <div className="flex items-center">
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                  COMPLETED - Hoàn thành
                </div>
              </Select.Option>
            </Select>
          </Form.Item>

          {/* Amount */}
          <Form.Item
            label="Số tiền rút"
            name="amount"
            rules={[
              { required: true, message: 'Vui lòng nhập số tiền' },
              { type: 'number', min: 1, message: 'Số tiền phải lớn hơn 0' },
              {
                type: 'number',
                max: 999999999,
                message: 'Số tiền không được vượt quá 999,999,999 VND',
              },
            ]}
          >
            <InputNumber
              placeholder="Nhập số tiền rút"
              className="w-full"
              formatter={(value) =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
              }
              min={0}
              onChange={handleAmountChange}
              addonAfter="VND"
            />
          </Form.Item>

          {/* Fee Applied (POS Fee) */}
          <Form.Item
            label={
              <span>
                Phí áp dụng <span className="text-gray-500">(% phí POS)</span>
              </span>
            }
            name="feeApplied"
            tooltip="Phí phần trăm từ POS được chọn"
          >
            <InputNumber
              placeholder="Chọn POS để hiển thị phí"
              className="w-full"
              min={0}
              step={0.01}
              precision={2}
              addonAfter="%"
              readOnly
            />
          </Form.Item>
        </div>

        {/* Submit Buttons */}
        <div className="flex justify-end space-x-2 pt-4 border-t">
          <Button onClick={handleClose}>Hủy</Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            icon={<ArrowDownOutlined />}
            className="bg-blue-500 hover:bg-blue-600"
          >
            Tạo giao dịch rút tiền
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default WithdrawalModal;
